import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Alert, 
  CircularProgress,
  Stack
} from '@mui/material';
import { Save } from 'lucide-react';
import SystemParameterService from '../../services/systemParameterService';
import ConfirmationDialog from '../../components/common/ConfirmationDialog';
import SuccessSnackbar from '../../components/common/SuccessSnackbar';

interface ParametersState {
  [key: string]: string;
}

const PARAM_KEYS = {
  SUSPENDED_DAYS: 'suspened_number_days',
  FEEDBACK_MONTHS: 'feedback_monitoring_months'
};

const ParametersPage = () => {
  const [parameters, setParameters] = useState<ParametersState>({
    [PARAM_KEYS.SUSPENDED_DAYS]: '',
    [PARAM_KEYS.FEEDBACK_MONTHS]: ''
  });
  const [initialParameters, setInitialParameters] = useState<ParametersState>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useEffect(() => { fetchParameters(); }, []);

  const fetchParameters = async () => {
    setLoading(true);
    setError(null);
    try {
      const allParams = await SystemParameterService.getAllParameters();
      const paramsMap: ParametersState = {};
      allParams.forEach((param: any) => {
        paramsMap[param.key] = param.value;
      });
      
      setParameters(prev => ({
        ...prev,
        [PARAM_KEYS.SUSPENDED_DAYS]: paramsMap[PARAM_KEYS.SUSPENDED_DAYS] || '',
        [PARAM_KEYS.FEEDBACK_MONTHS]: paramsMap[PARAM_KEYS.FEEDBACK_MONTHS] || '',
      }));
      setInitialParameters(paramsMap);

    } catch (err) {
      setError("Falha ao carregar parâmetros do sistema.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (/^\d*$/.test(value)) { 
        setParameters(prev => ({ ...prev, [name]: value }));
        if (snackbarMessage) setSnackbarMessage(null);
        if (error) setError(null);
    }
  };

  const handleConfirmSave = async () => {
    setIsConfirmDialogOpen(false);
    setSaving(true);
    setError(null);
    setSnackbarMessage(null);

    const changedParameters = Object.keys(parameters).filter(
      key => parameters[key] !== (initialParameters[key] || '')
    );

    if (changedParameters.length === 0) {
        setSaving(false);
        setSnackbarMessage("Nenhuma alteração para salvar.");
        setSnackbarOpen(true);
        return;
    }

    try {
      const updatePromises = changedParameters.map(key =>
        SystemParameterService.updateParameter(key, parameters[key])
      );
      await Promise.all(updatePromises);

      setInitialParameters(parameters); // Atualiza o estado inicial
      setSnackbarMessage("Parâmetros salvos com sucesso!");
      setSnackbarOpen(true);
    } catch (err: any) { 
      const apiErrorMessage = err?.response?.data?.message;
      setError(apiErrorMessage || 'Falha ao salvar parâmetros.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsConfirmDialogOpen(true);
  };

  const handleCancelSave = () => {
    setIsConfirmDialogOpen(false);
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        Parâmetros do Sistema
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      <Card elevation={2}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              name={PARAM_KEYS.SUSPENDED_DAYS}
              label="Dias de Suspensão por Contrato"
              value={parameters[PARAM_KEYS.SUSPENDED_DAYS]}
              onChange={handleChange}
              fullWidth
              required
              disabled={saving}
              margin="dense"
              size="small"
              sx={{mb: 2}}
              InputProps={{ style: { fontSize: '0.85rem' }, sx: { height: 40 } }}
              InputLabelProps={{ style: { fontSize: '0.85rem' } }}
              FormHelperTextProps={{ style: { fontSize: '0.75rem', marginTop: '2px' } }}
              helperText="Número de dias para suspensão que cada pessoa tem direito cada um ano de contrato"
            />

            <TextField
              name={PARAM_KEYS.FEEDBACK_MONTHS}
              label="Tempo em meses para aplicar feedback"
              value={parameters[PARAM_KEYS.FEEDBACK_MONTHS]}
              onChange={handleChange}
              fullWidth
              required
              disabled={saving}
              margin="dense"
              size="small"
              InputProps={{ style: { fontSize: '0.85rem' }, sx: { height: 40 } }}
              InputLabelProps={{ style: { fontSize: '0.85rem' } }}
              FormHelperTextProps={{ style: { fontSize: '0.75rem', marginTop: '2px' } }}
              helperText="Número de meses que o sistema monitora para alertar pessoas que precisam receber feedback"
            />
            
            <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save size={16} />}
                disabled={saving}
                size="small"
                sx={{ 
                  px: 2, 
                  py: 0.5,
                  height: 36,
                  fontWeight: 'bold',
                  fontSize: '0.75rem',
                  borderRadius: 1
                }}
              >
                {saving ? 'SALVANDO...' : 'SALVAR ALTERAÇÕES'}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={isConfirmDialogOpen}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        title="Confirmar Alterações"
        content="Tem certeza que deseja salvar as alterações nos parâmetros do sistema?"
        isDeleting={saving}
        confirmText={saving ? 'Salvando...' : 'Confirmar'}
      />

      <SuccessSnackbar
        open={snackbarOpen}
        message={snackbarMessage || ''}
        onClose={handleSnackbarClose}
      />
    </Box>
  );
};

export default ParametersPage; 